import { environment } from '@environments/environment';
import {
  OrganizationState,
  ActivityState,
  Activity,
  ActivityList,
  ParticipantList,
  ParticipantProduct,
  Payment,
  ArchiveList,
  PatchOperation,
  Notification,
  EmailType,
  ActivityItem,
} from '@app/_store';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  PatchParticipantResponse,
  SearchPaymentResponse,
  SendParticipantActivityLinkResponseStatus,
  SetActivityImageResponse,
} from './activity-api';

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  constructor(
    private http: HttpClient,
    private store: Store
  ) {}

  create(
    payload?: { name: string },
    type: string = null,
    templateId: string = null
  ): Observable<{ id: string; name: string; templateMessage: string; templateResponsible: string }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);
    return this.http.post<{ id: string; name: string; templateMessage: string; templateResponsible: string }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities?type=${type}&templateId=${templateId}`,
      payload
    );
  }

  getAll(): Observable<ActivityList> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<ActivityList>(`${environment.apiUrl}/organizations/${currentOrganization.id}/activities`);
  }

  get(id: string): Observable<Activity> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<Activity>(`${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${id}`);
  }

  getTemplates(): Observable<ActivityItem[]> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<ActivityItem[]>(`${environment.apiUrl}/organizations/${currentOrganization.id}/activities/templates`);
  }

  getParticipants(): Observable<ParticipantList> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.get<ParticipantList>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants`
    );
  }

  addProductToParticipant(
    participantId: string,
    payload: {
      productReferenceId?: string;
      title?: string;
      amount?: number;
      quantity: number;
      comment: string;
      options: { title: string; value: string }[];
      delivered: string;
      profit?: number;
    }
  ): Observable<ParticipantProduct> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.post<ParticipantProduct>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/products`,
      payload
    );
  }

  batchAddProductToParticipant(payload: {
    participantIds: string[];
    productReferenceId?: string;
    quantity: number;
  }): Observable<{ participantId: string; product: ParticipantProduct }[]> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.post<{ participantId: string; product: ParticipantProduct }[]>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/products`,
      payload
    );
  }

  markProductsAsDelivered(
    participantId: string,
    items: { participantId: string; productId: string }[]
  ): Observable<{ delivered: Date; deliveredBy: string }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<{ delivered: Date; deliveredBy: string }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/products/deliveries`,
      items
    );
  }

  addManualPayment(group: string, participantId: string, date: Date, amount: number, text: string): Observable<Payment> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.post<Payment>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/payments?group=${group}`,
      {
        date,
        amount,
        text,
      }
    );
  }

  deletePayment(participantId: string, paymentId: string): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/payments/${paymentId}`
    );
  }

  markPaymentAsVerified(participantId: string, paymentId: string): Observable<{ verified: Date; verifiedBy: string }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<{ verified: Date; verifiedBy: string }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/payments/${paymentId}/verification`,
      {}
    );
  }

  updateParticipantProduct(
    participantId: string,
    productId: string,
    payload: {
      title: string;
      amount: number;
      quantity: number;
      comment: string;
      options: { title: string; value: string }[];
      delivered: string;
      profit: number;
    }
  ): Observable<ParticipantProduct> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<ParticipantProduct>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/products/${productId}`,
      payload
    );
  }

  deleteParticipantProduct(participantId: string, productId: string, forceDelete: boolean) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/products/${productId}?force=${forceDelete}`
    );
  }

  deleteParticipant(group: string, participantId: string): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${group}/${participantId}`
    );
  }

  deleteActivity(): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}`
    );
  }

  deleteParticipantGroup(groupId: string): Observable<{ deleted: number }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete<{ deleted: number }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/groups/${groupId}`
    );
  }

  addParticipantsByTag(tag: string, includeEntireGroup: boolean): Observable<{ added: number }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<{ added: number }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/tags/${tag}?includeEntireGroup=${includeEntireGroup}`,
      null
    );
  }

  addParticipantsByMember(participantId: string, includeEntireGroup: boolean): Observable<{ added: number }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<{ added: number }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}?includeEntireGroup=${includeEntireGroup}`,
      null
    );
  }

  addParticipant(
    name: string,
    email: string,
    mobile: string,
    integrationData: unknown = undefined
  ): Observable<{ added: number }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.post<{ added: number }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants`,
      {
        name,
        email,
        mobile,
        integrationData,
      }
    );
  }

  searchPayment(reference: string): Observable<SearchPaymentResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<SearchPaymentResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/payments/search?reference=${reference}`
    );
  }

  getNotifications(emailType?: EmailType, group?: string): Observable<Notification[]> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    const query = emailType ? `?emailType=${emailType}` : '';
    group = group ? `/${group}` : '';

    return this.http.get<Notification[]>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/notifications${group}${query}`
    );
  }

  patchActivity(operations: PatchOperation[]): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);
    return this.http.patch<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}`,
      operations
    );
  }

  patchParticipant(group: string, participantId: string, operations: PatchOperation[]): Observable<PatchParticipantResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);
    return this.http.patch<PatchParticipantResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/${group}/participants/${participantId}`,
      operations
    );
  }

  sendParticipantActivityLink(
    participantId?: string,
    group?: string
  ): Observable<{ status: SendParticipantActivityLinkResponseStatus }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    let url: string;
    if (participantId) {
      url = `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/activitylink`;
    } else if (group) {
      url = `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/groups/${group}/activitylink`;
    } else {
      url = `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/activitylink`;
    }

    return this.http.put<{ status: SendParticipantActivityLinkResponseStatus }>(url, null);
  }

  getParticipantActivityLinkQRCode(participantId: string): Observable<string> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.get<string>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/participants/${participantId}/activitylink/qr`,
      { responseType: 'text' as 'json' }
    );
  }

  downloadReport(report: string) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.get<BlobPart>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/reports/${report}`,
      { responseType: 'blob' as 'json' }
    );
  }

  downloadArchive(archiveId: string) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<BlobPart>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/archives/${archiveId}/zip`,
      { responseType: 'blob' as 'json' }
    );
  }

  deleteArchive(archiveId: string) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.delete(`${environment.apiUrl}/organizations/${currentOrganization.id}/activities/archives/${archiveId}`);
  }

  downloadReceipt(group: string, participantId: string) {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.get<BlobPart>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/reports/activity-receipt/${group}/${participantId}`,
      { responseType: 'blob' as 'json' }
    );
  }

  exportActivity() {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.post<BlobPart>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/exports`,
      null,
      { responseType: 'blob' as 'json' }
    );
  }

  importActivity(data: FormData): Observable<{ id: string }> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.post<{ id: string }>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/imports`,
      data
    );
  }

  setActivityImage(file: File): Observable<SetActivityImageResponse> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    const formData: FormData = new FormData();
    formData.append('file', file);

    return this.http.put<SetActivityImageResponse>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/image`,
      formData
    );
  }

  deleteActivityImage(): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.delete<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/image`
    );
  }

  archiveActivity(): Observable<void> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    const currentActivity = this.store.selectSnapshot(ActivityState.currentActivity);

    return this.http.put<void>(
      `${environment.apiUrl}/organizations/${currentOrganization.id}/activities/${currentActivity.id}/archives`,
      null
    );
  }

  getArchives(): Observable<ArchiveList> {
    const currentOrganization = this.store.selectSnapshot(OrganizationState.current);

    return this.http.get<ArchiveList>(`${environment.apiUrl}/organizations/${currentOrganization.id}/activities/archives`);
  }
}
